import {
  Bot,
  ChevronsRight,
  CircleGauge,
  Expand,
  Maximize2,
  PanelRightOpen,
  ReceiptText,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {
  ChoiceCards,
  Segmented,
  SettingsCard,
  SettingsPageHeader,
  SettingsRow,
  SettingsScopeNote,
  SystemRule,
  Toggle,
} from './SettingsPrimitives';
import PreferenceSyncNotice from './PreferenceSyncNotice';
import usePreferenceNamespace from './usePreferenceNamespace';

const PANEL_OPTIONS = [
  { value: 'collapsed', label: '收起', description: '只显示状态和错误红点', icon: ChevronsRight },
  { value: 'floating', label: '浮层', description: '覆盖右侧，不挤压正文', icon: PanelRightOpen },
  { value: 'max', label: '最大化', description: '适合长对话和运行追踪', icon: Maximize2 },
];

const AGENT_DEFAULTS = {
  panelMode: 'floating',
  autoExpand: true,
  traceLevel: 'brief',
  confirmMode: 'high',
  thinkingMotion: true,
};

export default function AgentSettingsPanel({ preview = false, english = false }) {
  const {
    values,
    status,
    statusLabel,
    updatePreference,
    retry,
  } = usePreferenceNamespace('agent', AGENT_DEFAULTS, { enabled: !preview });
  const {
    panelMode,
    autoExpand,
    traceLevel,
    confirmMode,
    thinkingMotion,
  } = values;

  return (
    <div className={`account-content__stack creation-settings-page${status === 'loading' ? ' is-loading' : ''}`}>
      <SettingsPageHeader
        eyebrow="AGENT"
        title={english ? 'Agent Settings' : 'Agent 设置'}
        description={english ? 'Control how the Agent appears, how much run detail it shows, and when confirmation is required.' : '控制 Agent 如何出现、展示多少运行信息，以及何时需要你确认。'}
        status={english ? ({ preview: 'Personal settings', loading: 'Loading', ready: 'Synced', saving: 'Saving', saved: 'Saved', conflict: 'Review needed', error: '保存失败' }[status]) : statusLabel}
      />
      <PreferenceSyncNotice preview={preview} status={status} onRetry={retry} english={english} />

      <SettingsCard
        eyebrow="PANEL BEHAVIOR"
        title={english ? 'Default Panel Mode' : '默认面板形态'}
        description={english ? 'All three modes share the same conversation, input, and run state.' : '三种形态共享同一会话、输入和运行状态。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <ChoiceCards
          label={english ? 'Default Agent panel mode' : 'Agent 默认面板形态'}
          value={panelMode}
          options={english ? PANEL_OPTIONS.map((option, index) => ({ ...option, label: ['Collapsed', 'Floating', 'Maximized'][index], description: ['Show status and error indicator only', 'Overlay the right side without shrinking the script', 'Best for long conversations and run tracking'][index] })) : PANEL_OPTIONS}
          onChange={value => updatePreference('panelMode', value)}
        />
        <SettingsRow
          icon={Expand}
          title={english ? 'Expand results when a task finishes' : '任务完成后展开结果'}
          description={english ? 'Open the result summary automatically when a task finishes while collapsed.' : '收起状态下完成生成时，自动打开结果摘要。'}
        >
          <Toggle checked={autoExpand} onChange={value => updatePreference('autoExpand', value)} label={english ? 'Expand results when a task finishes' : '任务完成后展开结果'} />
        </SettingsRow>
      </SettingsCard>

      <SettingsCard
        eyebrow="TRANSPARENCY"
        title={english ? 'Run Information' : '运行信息'}
        description={english ? 'Show understandable stages during normal work and detailed tool steps only for troubleshooting.' : '普通创作只展示可理解的阶段；排障时再看详细工具步骤。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <SettingsRow
          icon={CircleGauge}
          title={english ? 'Run detail level' : '运行追踪层级'}
          description={english ? 'Choose a progress summary or more detail about the current task steps.' : '选择查看进度摘要，或了解更详细的任务步骤。'}
        >
          <Segmented
            label={english ? 'Run detail level' : '运行追踪层级'}
            value={traceLevel}
            options={[
              { value: 'brief', label: english ? 'Brief' : '简洁' },
              { value: 'detail', label: english ? 'Detailed' : '详细' },
            ]}
            onChange={value => updatePreference('traceLevel', value)}
          />
        </SettingsRow>
        <SettingsRow
          icon={Sparkles}
          title={english ? 'Thinking-state motion' : '思考状态动效'}
          description={english ? 'Show an animation while the Agent is thinking, searching, or generating.' : 'Agent 思考、检索和生成时显示动态提示。'}
        >
          <Toggle checked={thinkingMotion} onChange={value => updatePreference('thinkingMotion', value)} label={english ? 'Thinking-state motion' : '思考状态动效'} />
        </SettingsRow>
        <SettingsScopeNote>
          {english ? 'The system Reduce Motion setting always overrides this preference.' : '系统“减少动态效果”开启时，始终覆盖这里的动效偏好。'}
        </SettingsScopeNote>
      </SettingsCard>

      <SettingsCard
        eyebrow="CONFIRMATION"
        title={english ? 'Action Confirmation' : '执行确认'}
        description={english ? 'Paid or irreversible actions require clear boundaries and can never run silently.' : '付费或不可逆动作必须有清晰边界，不能由 Agent 静默执行。'}
        aside={english ? 'Safety' : '安全'}
      >
        <SettingsRow
          icon={ReceiptText}
          title={english ? 'Paid-action confirmation' : '付费动作确认'}
          description={english ? 'Review the estimated credit cost before confirming a paid action.' : '确认付费操作时，会显示预计消耗点数。'}
        >
          <Segmented
            label={english ? 'Paid-action confirmation policy' : '付费动作确认策略'}
            value={confirmMode}
            options={[
              { value: 'all', label: english ? 'Confirm every time' : '每次确认' },
              { value: 'high', label: english ? 'Confirm high-cost actions' : '高消耗确认' },
            ]}
            onChange={value => updatePreference('confirmMode', value)}
          />
        </SettingsRow>
        <div className="creation-system-rules">
          <SystemRule
            title={english ? 'Risky actions require confirmation' : '高风险动作需二次确认'}
            description={english ? 'Accepting a draft that has not passed checks and deleting items in bulk each require a separate confirmation.' : '强制采用未通过检查的候选稿、批量删除时，均需单独确认。'}
            status={english ? 'Always on' : '不可关闭'}
          />
          <SystemRule
            title={english ? 'Automatic model selection' : '系统自动选择模型'}
            description={english ? 'The system selects a suitable model for each task. No extra configuration is needed.' : '系统根据任务选择合适的模型，无需额外配置。'}
            status={english ? 'Managed' : '平台托管'}
          />
        </div>
      </SettingsCard>

      <SettingsCard
        eyebrow="MODEL ACCESS"
        title={english ? 'Model Access' : '模型访问'}
        description={english ? 'Models are selected automatically for writing, story checks, and guidance.' : '写作、剧情检查和创作引导均由系统自动选择模型。'}
      >
        <div className="creation-model-access">
          <article>
            <span><Bot size={17} /></span>
            <div><strong>{english ? 'Writing & story checks' : '写作与剧情检查'}</strong><small>{english ? 'Prioritize script quality and story consistency' : '优先保证剧本质量与前后剧情一致'}</small></div>
            <em>{english ? 'Managed' : '平台托管'}</em>
          </article>
          <article>
            <span><ShieldCheck size={17} /></span>
            <div><strong>{english ? 'Creative guidance & organization' : '创作引导与内容整理'}</strong><small>{english ? 'Balance response speed and content quality' : '兼顾响应速度与内容质量'}</small></div>
            <em>{english ? 'Managed' : '平台托管'}</em>
          </article>
        </div>
      </SettingsCard>
    </div>
  );
}
